import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Award, Wallet, Lock, TrendingUp, Send, Receipt, Image as ImageIcon } from "lucide-react";
import StatCard from "../components/agentProgram/StatCard";
import StatusPill from "../components/agentProgram/StatusPill";
import EmptyState from "../components/agentProgram/EmptyState";

const API = process.env.REACT_APP_API_URL;
const SERVER_ROOT = (API || "").replace(/\/api\/?$/, "");

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const requestStatusLabel = {
  pending: "Pending",
  fulfilled: "Fulfilled",
  dismissed: "Dismissed",
};

const statusLabel = {
  scheduled: "Scheduled",
  payable: "Payable",
  paid: "Paid",
  held: "Held",
  released: "Released",
  clawed_back: "Clawed back",
  forfeited: "Forfeited",
};

export default function AgentProgramDashboard() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [levels, setLevels] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState({ requests: [], releaseBatches: [] });
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [message, setMessage] = useState("");

  const agentId = userInfo?.merchantId || "";
  const authConfig = {
    headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {},
  };

  const fetchData = async () => {
    if (!agentId) return;
    setError("");
    try {
      const [{ data: summaryData }, { data: txnData }, { data: configData }, { data: historyData }] = await Promise.all([
        axios.get(`${API}/agent-program/agents/${agentId}/summary`, authConfig),
        axios.get(`${API}/agent-program/agents/${agentId}/transactions`, authConfig),
        axios.get(`${API}/agent-program/config`, authConfig),
        axios.get(`${API}/agent-program/agents/${agentId}/payout-history`, authConfig),
      ]);
      setSummary(summaryData);
      setTransactions(txnData || []);
      setLevels((configData?.levels || []).slice().sort((a, b) => a.level - b.level));
      setPayoutHistory({ requests: historyData?.requests || [], releaseBatches: historyData?.releaseBatches || [] });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load Agent Program dashboard");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const requestPayout = async () => {
    setRequesting(true);
    setMessage("");
    setError("");
    try {
      const { data } = await axios.post(`${API}/agent-program/agents/${agentId}/request-payout`, {}, authConfig);
      setSummary(data);
      setMessage("Payout requested — an admin will review and release it.");
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to request payout");
    } finally {
      setRequesting(false);
    }
  };

  if (!agentId) {
    return (
      <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-lg">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Agent Program unavailable</h1>
          <p className="text-sm text-gray-500">This login is not linked with a merchant account.</p>
        </div>
      </div>
    );
  }

  const s = summary || {};

  let progressCaption = "";
  const currentIdx = levels.findIndex((l) => l.level === s.level);
  if (currentIdx >= 0 && currentIdx < levels.length - 1) {
    const remaining = Math.max(0, levels[currentIdx + 1].minPoints - (s.unlockedPoints || 0));
    progressCaption = `${remaining} pts to Level ${levels[currentIdx + 1].level}`;
  } else if (currentIdx === levels.length - 1 && levels.length > 0) {
    progressCaption = "Top level reached";
  }

  const payoutTimeline = [
    ...payoutHistory.requests.map((r) => ({ kind: "request", at: r.requestedAt, data: r })),
    ...payoutHistory.releaseBatches.map((b) => ({ kind: "release", at: b.releasedAt, data: b })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-start sm:justify-between w-full">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Agent Program</h1>
          <p className="text-sm text-gray-500 mt-1">{userInfo?.name || userInfo?.merchantName || "Agent"}</p>
        </div>
      </div>

      {message && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-3 text-sm mb-4">
          {message}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-5">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-3">
        <StatCard title="Level" value={`Level ${s.level ?? 0}`} helper={progressCaption} icon={Award} tone="bg-indigo-50 text-indigo-600" />
        <StatCard
          title="Unlocked Points"
          value={s.unlockedPoints ?? 0}
          helper={`${s.lockedPoints ?? 0} pts locked`}
          icon={TrendingUp}
          tone="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          title="Available Balance"
          value={`₹${money(s.availableBalance)}`}
          helper="Withdrawable now"
          icon={Wallet}
          tone="bg-blue-50 text-blue-600"
        />
        <StatCard
          title="Held Balance"
          value={`₹${money(s.heldBalance)}`}
          helper="Releases automatically 6 months after disbursal"
          icon={Lock}
          tone="bg-amber-50 text-amber-600"
        />
      </div>

      {s.availableBalance > 0 && (
        <div className="mb-5">
          {s.payoutRequestedAt ? (
            <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm">
              <Send size={16} className="flex-shrink-0" />
              Payout requested on {dateText(s.payoutRequestedAt)} — awaiting admin release.
            </div>
          ) : (
            <button
              onClick={requestPayout}
              disabled={requesting}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm hover:shadow transition-all"
            >
              <Send size={15} /> {requesting ? "Requesting…" : "Request Payout"}
            </button>
          )}
        </div>
      )}

      {s.outstandingShortfall > 0 && (
        <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-4 py-3 text-sm mb-5">
          Outstanding shortfall of ₹{money(s.outstandingShortfall)} from a prior clawback is being recovered
          from future earnings before new payouts are released.
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Recent Transactions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {["#", "Type", "Gross", "TDS", "Net", "Available", "Held", "Status", "Date"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <EmptyState icon={Receipt} title="No transactions yet" compact />
                  </td>
                </tr>
              ) : (
                transactions.map((txn) => (
                  <tr key={txn._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{txn.serialNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap capitalize">
                      {txn.type.replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">₹{money(txn.grossAmount)}</td>
                    <td className="px-4 py-3 text-sm text-red-500 whitespace-nowrap">₹{money(txn.tdsAmount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium whitespace-nowrap">₹{money(txn.netAmount)}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 whitespace-nowrap">₹{money(txn.availableAmount)}</td>
                    <td className="px-4 py-3 text-sm text-amber-600 whitespace-nowrap">
                      ₹{money(txn.heldAmount)}
                      {txn.heldReleaseDate && txn.status === "held" && (
                        <span className="block text-[11px] text-gray-400">
                          releases {dateText(txn.heldReleaseDate)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap capitalize">
                      {statusLabel[txn.status] || txn.status}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{dateText(txn.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0 mt-5">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Payout History</h3>
        </div>
        {payoutTimeline.length === 0 ? (
          <EmptyState icon={ImageIcon} title="No payout activity yet" compact />
        ) : (
          <div className="divide-y divide-gray-100">
            {payoutTimeline.map((item, idx) =>
              item.kind === "request" ? (
                <div key={`req-${item.data._id || idx}`} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-800">Requested ₹{money(item.data.requestedAmount)}</div>
                    <div className="text-xs text-gray-400">{dateText(item.data.requestedAt)}</div>
                  </div>
                  <StatusPill
                    label={requestStatusLabel[item.data.status] || item.data.status}
                    tone={item.data.status === "pending" ? "warning" : item.data.status === "fulfilled" ? "success" : "neutral"}
                  />
                </div>
              ) : (
                <div key={`rel-${item.data._id || idx}`} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-800">
                      Released ₹{money(item.data.netAmount)}
                      {item.data.shortfallRecovered > 0 && (
                        <span className="text-xs text-orange-500"> (₹{money(item.data.shortfallRecovered)} recovered from shortfall)</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      batch #{item.data.serialNumber} · {dateText(item.data.releasedAt)}
                    </div>
                  </div>
                  {item.data.screenshotUrl && (
                    <a
                      href={`${SERVER_ROOT}${item.data.screenshotUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      <ImageIcon size={14} /> View proof
                    </a>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
